// Owns the workspace "⋯" recent-views menu: the deduped, paginated list of every distinct view
// this session has landed on, and the single `record` entry point every caller uses to add one.
//
// Two write paths converge on ONE list:
//   • agent-driven views are recorded server-side by `publish_action` — the client only mirrors
//     them locally (optimistic, so the menu updates before the socket round-trip settles);
//   • user-driven views (a manual filter/facet/tab click, a `+` create, replaying an entry from
//     the menu) have no agent turn behind them, so those POST to the recent-views endpoint.
// Both derive the same `recentViewKey`, so the two paths collapse onto one row rather than
// producing a near-duplicate pair.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLazyListRecentViewsQuery,
  useRecordRecentViewMutation,
  type RecentViewRow,
} from '@/redux/internal-services/agentApi';
import {
  recentViewKey,
  normalizeViewParams,
  upsertRecent,
  type RecentEntry,
  type RecentContextRef,
  type RecentViewSource,
} from '@/components/chat/recentViews';
import type { SamModule } from '@/components/SAM/samActionTypes';

/** Fallback when the caller hasn't resolved `chat_settings.recent_views_limit` yet (e.g. the
 *  config query is still in flight on first mount) — matches the backend's own default. */
const DEFAULT_LIMIT = 10;

export interface RecordViewInput {
  cardType: SamModule;
  kind?: string;
  title?: string;
  subtitle?: string;
  contextRef?: RecentContextRef | null;
  viewParams?: Record<string, unknown> | null;
  /** Defaults to 'agent'. Anything other than 'agent' is also POSTed to the server, because only
   *  the agent path is already persisted by `publish_action`. */
  source?: RecentViewSource;
  messageId?: string;
}

export interface RecentViews {
  /** Newest-picked first — the order the "⋯" menu renders. */
  entries: RecentEntry[];
  /** Whether an older page remains (drives the menu's scroll sentinel). */
  hasMore: boolean;
  loadMore: () => void;
  /** Add (or bump) a view. Safe to call on every render path — identical views collapse. */
  record: (input: RecordViewInput) => void;
  /** Drop everything and re-read from the server (called when the session changes). */
  reset: () => void;
}

function toEntry(row: RecentViewRow): RecentEntry {
  const contextRef = row.context_ref && row.context_ref.type ? row.context_ref : undefined;
  const viewParams = normalizeViewParams(row.view_params);
  return {
    // Recomputed rather than taken from `entry_key`: the server stores a sha1, the client keys on
    // the canonical string. Same inputs → the two always agree on identity.
    key: recentViewKey({ cardType: row.card_type, contextRef, viewParams }),
    cardType: row.card_type as SamModule,
    kind: row.kind || '',
    title: row.title || '',
    subtitle: row.subtitle || undefined,
    contextRef: contextRef || undefined,
    viewParams,
    source: (row.source || 'agent') as RecentViewSource,
    messageId: row.message_id || undefined,
    lastAccessedAt: row.last_accessed_at_ms
      ?? (row.last_accessed_at ? new Date(row.last_accessed_at).getTime() : Date.now()),
  };
}

export function useRecentViews(conversationId: string, limit: number = DEFAULT_LIMIT): RecentViews {
  // Never below 1 — a misconfigured/zero value must not silently hide the menu entirely.
  const cap = Math.max(1, limit || DEFAULT_LIMIT);
  const [entries, setEntries] = useState<RecentEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [fetchPage] = useLazyListRecentViewsQuery();
  const [postView] = useRecordRecentViewMutation();
  // How many rows have been pulled so far — the next page's `skip`.
  const loadedRef = useRef(0);
  // Which conversation a page fetch is currently in flight FOR (null = none). Conversation-scoped
  // rather than a bare boolean: a fetch still running for a PREVIOUS conversation must not suppress
  // the new one. It used to — switching sessions mid-fetch made the [conversationId] effect's own
  // loadPage(0) bail on the shared flag, then the old response hit the staleness guard below and
  // returned, so nothing ever fetched the new session's history. `loadMore` couldn't rescue it
  // either, since reset() sets hasMore=false and that gates it.
  const loadingForRef = useRef<string | null>(null);
  const conversationRef = useRef(conversationId);
  conversationRef.current = conversationId;
  // Mirrors `entries` so `record` can merge against the current list without taking it as a dep —
  // ChatPage passes `record` straight into other hooks, so its identity must stay stable.
  const entriesRef = useRef<RecentEntry[]>(entries);
  entriesRef.current = entries;

  const loadPage = useCallback(async (skip: number) => {
    const id = conversationRef.current;
    if (!id) return;
    if (skip >= cap) return; // already at the configured ceiling — nothing more to show
    if (loadingForRef.current === id) return; // a page for THIS conversation is already in flight
    loadingForRef.current = id;
    try {
      // Never request past the ceiling, even if a page-sized fetch would overshoot it.
      const pageLimit = Math.min(cap - skip, cap);
      const { rows, total } = await fetchPage({ conversationId: id, skip, limit: pageLimit }).unwrap();
      // The session can change while a page is in flight — drop a response for the old one.
      if (conversationRef.current !== id) return;
      const page = rows.map(toEntry);
      setEntries((prev) => {
        // Merge rather than concat: an optimistic local entry for a row in this page must not
        // appear twice, and the server's copy (with its real timestamp) wins.
        const byKey = new Map(prev.map((e) => [e.key, e]));
        for (const e of page) byKey.set(e.key, e);
        // Trimmed to `cap` here too, not just below on the `record()` path — a page fetched right
        // after the limit was lowered (or after other entries were recorded locally) could
        // otherwise leave more than `cap` rows in state until the next reload.
        return [...byKey.values()].sort((a, b) => b.lastAccessedAt - a.lastAccessedAt).slice(0, cap);
      });
      loadedRef.current = skip + rows.length;
      setHasMore(loadedRef.current < total && loadedRef.current < cap);
    } catch {
      // A brand-new session has no recent views yet (404 until the first turn creates it) —
      // an empty menu is the correct outcome, not an error to surface.
      if (conversationRef.current === id) setHasMore(false);
    } finally {
      // Release only if a newer conversation's load hasn't already claimed the slot.
      if (loadingForRef.current === id) loadingForRef.current = null;
    }
  }, [fetchPage, cap]);

  const reset = useCallback(() => {
    setEntries([]);
    setHasMore(false);
    loadedRef.current = 0;
  }, []);

  // Load the first page whenever the open session changes, or the configured cap does (e.g. the
  // chat-config query resolves a beat after mount with a value other than DEFAULT_LIMIT).
  useEffect(() => {
    reset();
    if (conversationId) loadPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, cap]);

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    loadPage(loadedRef.current);
  }, [hasMore, loadPage]);

  const record = useCallback((input: RecordViewInput) => {
    const source = input.source ?? 'agent';
    const contextRef = input.contextRef && input.contextRef.type ? input.contextRef : undefined;
    const viewParams = normalizeViewParams(input.viewParams);
    const key = recentViewKey({ cardType: input.cardType, contextRef, viewParams });
    // Carry over anything this call doesn't supply from the entry already on screen — a manual
    // filter change on an agent-titled card sends no title/caption/kind, and the menu should never
    // regress to a blank heading. Resolved ONCE here, outside the state updater, so the optimistic
    // row and the POST below agree: the POST used to send the raw (empty) input while only the
    // local row got the merge, which blanked the persisted `kind` on every manual filter tweak.
    const existing = entriesRef.current.find((e) => e.key === key);
    const entry: RecentEntry = {
      key,
      cardType: input.cardType,
      kind: input.kind || existing?.kind || '',
      title: input.title || existing?.title || '',
      subtitle: input.subtitle || existing?.subtitle,
      contextRef,
      viewParams,
      source,
      messageId: input.messageId || existing?.messageId,
      lastAccessedAt: Date.now(),
    };
    // Optimistic: the menu reflects the view the moment it opens, on either path. `upsertRecent`
    // always puts the touched entry first, so trimming to `cap` here only ever drops whichever
    // entry is now the OLDEST — exactly "only show the latest N".
    setEntries((prev) => upsertRecent(prev, entry).slice(0, cap));
    if (source === 'agent') return; // already persisted server-side by publish_action
    const id = conversationRef.current;
    if (!id) return;
    postView({
      conversationId: id,
      card_type: entry.cardType,
      kind: entry.kind,
      title: entry.title,
      subtitle: entry.subtitle,
      context_ref: entry.contextRef ?? null,
      view_params: entry.viewParams,
      source,
      message_id: entry.messageId ?? null,
    }).unwrap().catch(() => {
      // The optimistic entry stays on screen for this session; it just won't survive a reload.
    });
  }, [postView, cap]);

  return { entries, hasMore, loadMore, record, reset };
}
